import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  IconButton,
  Divider,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import ShieldIcon from '@mui/icons-material/Shield';
import LinkIcon from '@mui/icons-material/Link';
import PaymentsIcon from '@mui/icons-material/Payments';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { logout } from '../store/authSlice';
import { authLogout } from '../services/authService';

const DRAWER_WIDTH = 260;
const DRAWER_COLLAPSED = 72;

const navItems = [
  { label: 'Athletes', path: '/athletes', icon: <PeopleAltIcon /> },
  { label: 'Onboarding', path: '/onboarding', icon: <HowToRegIcon /> },
  { label: 'Workouts', path: '/workouts', icon: <FitnessCenterIcon /> },
  { label: 'Moderation', path: '/moderation', icon: <ShieldIcon /> },
  { label: 'Referrals', path: '/referrals', icon: <LinkIcon /> },
  { label: 'Payments', path: '/payments', icon: <PaymentsIcon /> },
  { label: 'Competitions', path: '/competitions', icon: <EmojiEventsIcon /> },
  { label: 'Agents', path: '/agents', icon: <SupportAgentIcon /> },
  { label: 'Agent Payouts', path: '/agent-payouts', icon: <AccountBalanceWalletIcon /> },
];

const Layout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);

  const drawerWidth = collapsed ? DRAWER_COLLAPSED : DRAWER_WIDTH;

  const handleLogout = async () => {
    try {
      await authLogout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      dispatch(logout());
    }
  };

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: '#14161c',
        borderRight: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      {/* Logo area */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          px: collapsed ? 1 : 2.5,
          py: 2.5,
          minHeight: 72,
        }}
      >
        {!collapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #2bb6a9 0%, #1e8a80 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                fontSize: '1.1rem',
                color: '#fff',
              }}
            >
              T
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.2rem', color: '#e4e6eb' }}>
              Thravos
            </Typography>
          </Box>
        )}
        {collapsed && (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #2bb6a9 0%, #1e8a80 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 800,
              fontSize: '1.1rem',
              color: '#fff',
            }}
          >
            T
          </Box>
        )}
        {!isMobile && !collapsed && (
          <IconButton size="small" onClick={() => setCollapsed(true)} sx={{ color: '#8b949e' }}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

      {/* Nav items */}
      <List sx={{ px: 1, py: 1.5, flex: 1 }}>
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Tooltip key={item.path} title={collapsed ? item.label : ''} placement="right" arrow>
              <ListItemButton
                component={NavLink}
                to={item.path}
                onClick={() => isMobile && setMobileOpen(false)}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  px: collapsed ? 2 : 2,
                  py: 1,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  bgcolor: isActive ? 'rgba(43, 182, 169, 0.12)' : 'transparent',
                  color: isActive ? '#2bb6a9' : '#8b949e',
                  '&:hover': {
                    bgcolor: isActive
                      ? 'rgba(43, 182, 169, 0.16)'
                      : 'rgba(255,255,255,0.04)',
                    color: '#e4e6eb',
                  },
                  transition: 'all 0.2s',
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: collapsed ? 0 : 40,
                    color: 'inherit',
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: isActive ? 600 : 500,
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          );
        })}
      </List>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

      {/* User section */}
      {user && (
        <Box sx={{ px: collapsed ? 1 : 2, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              width: 34,
              height: 34,
              bgcolor: '#2bb6a9',
              fontSize: '0.85rem',
              fontWeight: 700,
            }}
          >
            {(user.name || user.email || '?')[0].toUpperCase()}
          </Avatar>
          {!collapsed && (
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: '#e4e6eb', fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {user.name || user.email}
              </Typography>
              <Typography variant="caption" sx={{ color: '#8b949e', fontSize: '0.7rem' }}>
                Admin
              </Typography>
            </Box>
          )}
          <Tooltip title="Logout" placement="right">
            <IconButton size="small" onClick={handleLogout} sx={{ color: '#8b949e', '&:hover': { color: '#f44336' } }}>
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Mobile hamburger */}
      {isMobile && (
        <IconButton
          onClick={() => setMobileOpen(true)}
          sx={{
            position: 'fixed',
            top: 12,
            left: 12,
            zIndex: 1300,
            bgcolor: '#14161c',
            border: '1px solid rgba(255,255,255,0.08)',
            color: '#e4e6eb',
            '&:hover': { bgcolor: '#1a1d23' },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Sidebar */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              border: 'none',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            transition: 'width 0.2s',
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              transition: 'width 0.2s',
              border: 'none',
              overflow: 'hidden',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          bgcolor: '#0f1117',
          transition: 'margin-left 0.2s',
        }}
      >
        {/* Top bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 2, md: 4 },
            py: 2,
            borderBottom: '1px solid rgba(255,255,255,0.04)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!isMobile && collapsed && (
              <IconButton size="small" onClick={() => setCollapsed(false)} sx={{ color: '#8b949e', mr: 1 }}>
                <MenuIcon fontSize="small" />
              </IconButton>
            )}
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#e4e6eb' }}>
              {navItems.find((n) => n.path === location.pathname)?.label || 'Dashboard'}
            </Typography>
          </Box>
        </Box>

        {/* Page content */}
        <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
